// src/ui/MyQRModal.jsx — my contact QR, the one people scan in the app.
//
// 🟥 THE PAYLOAD IS THE APP'S, EXACTLY. HomeScreen.UserQRCodeModal encodes
//    JSON.stringify({ type: 'allo_contact', uid, username }) — so this has to
//    produce the same string, character for character, or the phone's scanner
//    looks at a code it doesn't recognise. Nothing about it is web-specific.
//
// Download works by serialising the rendered SVG, painting it onto a canvas at
// 3× for a crisp file, and saving the PNG — the browser equivalent of the app's
// "save to gallery" snapshot, with the same white frame and caption baked in so
// the saved image is presentable on its own.

import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Check, Alert } from './icons';

const SIZE = 232;

export default function MyQRModal({ myUid, myProfile, onClose }) {
  const wrapRef = useRef(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const name = myProfile?.fullName || myProfile?.username || 'Allo user';
  const username = myProfile?.username || '';
  const payload = JSON.stringify({ type: 'allo_contact', uid: myUid, username });

  const download = async () => {
    const svg = wrapRef.current?.querySelector('svg');
    if (!svg) return;
    const scale = 3;
    const pad = 28;
    const capH = 54;
    const w = SIZE + pad * 2;
    const h = SIZE + pad * 2 + capH;

    const xml = new XMLSerializer().serializeToString(svg);
    const url = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(xml)))}`;

    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = w * scale; c.height = h * scale;
      const ctx = c.getContext('2d');
      ctx.scale(scale, scale);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, pad, pad, SIZE, SIZE);
      // the caption, so the saved file explains itself
      ctx.fillStyle = '#0b1220';
      ctx.font = '600 15px -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(name, w / 2, SIZE + pad + 26);
      if (username) {
        ctx.fillStyle = '#667085';
        ctx.font = '500 12.5px -apple-system, Segoe UI, Roboto, sans-serif';
        ctx.fillText(`@${username} · scan in Allo`, w / 2, SIZE + pad + 45);
      }
      const a = document.createElement('a');
      a.href = c.toDataURL('image/png');
      a.download = `allo-${(username || 'contact').replace(/[^a-z0-9]+/gi, '-')}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setSaved(true);
      setTimeout(() => setSaved(false), 2600);
    };
    img.src = url;
  };

  return (
    <div className="ng-modal" onClick={onClose}>
      <div className="ng-card qr-card" onClick={(e) => e.stopPropagation()}>
        <div className="ng-head">
          <b>My QR code</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="qr-body">
          <div className="qr-frame" ref={wrapRef}>
            <QRCodeSVG value={payload} size={SIZE} level="M" bgColor="#ffffff" fgColor="#0b1220" />
          </div>

          <div className="qr-who">
            <b>{name}</b>
            {username ? <i>@{username}</i> : null}
          </div>

          <div className="qr-warn">
            <Alert width={15} height={15} />
            <span>Anyone who scans this can start a chat with you. Share it only with people you trust.</span>
          </div>
        </div>

        <div className="qr-acts">
          <button className="lg-go" onClick={download}>
            {saved ? <><Check width={15} height={15} /> Saved</> : <><Download width={15} height={15} /> Download PNG</>}
          </button>
          <button
            className="ci-chip"
            onClick={() => {
              navigator.clipboard?.writeText(username ? `@${username}` : myUid).catch(() => {});
              setCopied(true); setTimeout(() => setCopied(false), 1800);
            }}
          >
            {copied ? 'Copied' : (username ? 'Copy @username' : 'Copy my ID')}
          </button>
        </div>
      </div>
    </div>
  );
}
